import { setStorage, removeStorage, getStorage, forceFlushStorage, STORAGE_KEYS } from './storage';
import VidaaTVService from '@/services/vidaaTVService';

/**
 * WebOS device information interface
 * Fields marked Required per official webOSTV.js docs are non-optional at runtime
 * (older platforms may still return undefined for some fields despite "Required" label)
 * Ref: https://webostv.developer.lge.com/develop/references/webostvjs-webos#deviceinfo
 */
interface WebOSDeviceInfo {
  modelName?: string;
  version?: string;
  versionMajor?: number;
  versionMinor?: number;
  versionDot?: number;
  sdkVersion?: string;
  /** Pixels for VIDEO PLAYBACK — use window.innerWidth for graphics/app resolution */
  screenWidth?: number;
  /** Pixels for VIDEO PLAYBACK — use window.innerHeight for graphics/app resolution */
  screenHeight?: number;
  uhd?: boolean;
  uhd8K?: boolean;
  /** Indicate whether the device supports HDR10 (Required per docs) */
  hdr10?: boolean;
  /** Indicate whether the device supports Dolby Vision (Required per docs) */
  dolbyVision?: boolean;
  /** Indicate whether the device supports Dolby Atmos. From webOSTV.js v1.2.6+ */
  dolbyAtmos?: boolean;
  brandName?: string;
  manufacturer?: string;
  platformBizType?: string;
  tuner?: boolean;
  oled?: boolean;
  ddrSize?: string;
  mainboardMaker?: string;
}

/**
 * PalmSystem device information interface
 */
interface PalmSystemDeviceInfo {
  modelName?: string;
  platformVersion?: string;
  screenWidth?: number;
  screenHeight?: number;
}

/**
 * Tizen system capability interface
 */
interface TizenCapabilities {
  screenWidth?: number;
  hdr?: boolean;
}

/**
 * Device information interface
 */
export interface DeviceInfo {
  platform: 'webos' | 'tizen' | 'vidaa' | 'playstation' | 'unknown';
  deviceId?: string;
  modelName?: string;
  version?: string;
  sdkVersion?: string;
  manufacturer?: 'LG' | 'Samsung';
  screenWidth?: number;
  screenHeight?: number;
  uhd?: boolean;
  /** WebOS: 8K UHD support */
  uhd8K?: boolean;
  hdr?: boolean;
  /** HDR10 support */
  hdr10?: boolean;
  /** HLG support (e.g. VIDAA, webOS Luna hdrType) */
  hlg?: boolean;
  /** Dolby Vision support */
  dolbyVision?: boolean;
  /** Dolby Atmos support (webOS). From webOSTV.js v1.2.6+ */
  dolbyAtmos?: boolean;
  /** Tizen: HDR10+ support */
  hdr10Plus?: boolean;
  mac?: string;
}
/** In-flight uncached fetch; cached callers await this so they never return stale storage. */
let deviceInfoFetchPromise: Promise<DeviceInfo> | null = null;

/**
 * Device information storage
 */
/**
 * Parse the return value of Hisense_GetSupportForHDR().
 * Per VIDAA docs the function returns a numeric STRING: 0=NOT supported, 1=HDR10, 2=HLG, 3=HDR10+HLG, 4=HDR10+HLG+HDR10+.
 * Dolby Vision is NOT reported by this API — use getDeviceCapability() / getDeviceInfo() separately.
 */
const parseVidaaHDRSupport = (raw: unknown): { hdr: boolean; hdr10: boolean; hlg: boolean; hdr10Plus: boolean } => {
  const code = parseInt(String(raw), 10);
  if (isNaN(code) || code === 0) return { hdr: false, hdr10: false, hlg: false, hdr10Plus: false };
  return {
    hdr: true,
    hdr10: code === 1 || code === 3 || code === 4,
    hlg: code === 2 || code === 3 || code === 4,
    hdr10Plus: code === 4,
  };
};

/**
 * Parse Hisense_GetResolution() return: "framewidth,frameheight,interlaced,framerate"
 */
const parseVidaaResolution = (raw: unknown): { width?: number; height?: number } => {
  if (!raw || typeof raw !== 'string') return {};
  const parts = raw.split(',').map(p => parseInt(p.trim(), 10));
  const [width, height] = parts;
  return {
    width: Number.isFinite(width) && width > 0 ? width : undefined,
    height: Number.isFinite(height) && height > 0 ? height : undefined,
  };
};

/**
 * Get Vidaa device information.
 * API priority: Hisense_Get4KSupportState(), Hisense_GetSupportForHDR() (0-4), Hisense_GetResolution(),
 * then Hisense.getDeviceCapability() / vidaa.getDeviceInfo() for Dolby Vision.
 */
const getVidaaDeviceInfo = async (): Promise<DeviceInfo> => {
  try {
    const vidaaService = VidaaTVService.getInstance();
    await vidaaService.initialize();
    const VidaaTV = vidaaService.getVidaaTV();

    const deviceInfo: DeviceInfo = { platform: 'vidaa' };

    try {
      deviceInfo.deviceId = VidaaTV.getDeviceID();
    } catch (e) {
      console.warn('[VIDAA] Failed to get device ID:', e);
    }
    try {
      deviceInfo.manufacturer = VidaaTV.getBrand();
    } catch (e) {
      console.warn('[VIDAA] Failed to get brand:', e);
    }
    try {
      deviceInfo.modelName = VidaaTV.getModelName();
    } catch (e) {
      console.warn('[VIDAA] Failed to get model name:', e);
    }
    try {
      deviceInfo.version = VidaaTV.getFirmWareVersion();
    } catch (e) {
      console.warn('[VIDAA] Failed to get firmware version:', e);
    }

    try {
      deviceInfo.uhd = VidaaTV.get4KState();
    } catch (e) {
      console.warn('[VIDAA] Failed to get 4K state:', e);
    }

    try {
      const rawHDR = VidaaTV.getHDRStatus();
      const hdrCaps = parseVidaaHDRSupport(rawHDR);
      deviceInfo.hdr = hdrCaps.hdr;
      deviceInfo.hdr10 = hdrCaps.hdr10 || undefined;
      deviceInfo.hlg = hdrCaps.hlg || undefined;
      deviceInfo.hdr10Plus = hdrCaps.hdr10Plus || undefined;
    } catch (e) {
      console.warn('[VIDAA] Failed to get HDR status:', e);
    }

    if (typeof window !== 'undefined') {
      try {
        const rawRes = (window as any).Hisense_GetResolution?.();
        const { width, height } = parseVidaaResolution(rawRes);
        if (width) deviceInfo.screenWidth = width;
        if (height) deviceInfo.screenHeight = height;
      } catch (e) {
        console.warn('[VIDAA] Failed to get resolution:', e);
      }

      try {
        const caps = (window as any).Hisense?.getDeviceCapability?.();
        if (caps && typeof caps === 'object') {
          if (deviceInfo.hdr === undefined && caps.hdr !== undefined) deviceInfo.hdr = !!caps.hdr;
          if (deviceInfo.hdr10 === undefined && caps.hdr10 !== undefined) deviceInfo.hdr10 = !!caps.hdr10;
          if (caps.dolbyVision !== undefined) deviceInfo.dolbyVision = !!caps.dolbyVision;
        }
      } catch (e) {
        console.warn('[VIDAA] Hisense.getDeviceCapability() failed:', e);
      }

      try {
        const devInfo = (window as any).vidaa?.getDeviceInfo?.();
        if (devInfo && typeof devInfo === 'object') {
          if (deviceInfo.hdr === undefined && devInfo.hdrSupport !== undefined) deviceInfo.hdr = !!devInfo.hdrSupport;
          if (devInfo.dolbyVisionSupport !== undefined) deviceInfo.dolbyVision = !!devInfo.dolbyVisionSupport;
        }
      } catch (e) {
        console.warn('[VIDAA] vidaa.getDeviceInfo() failed:', e);
      }

      deviceInfo.screenWidth = deviceInfo.screenWidth ?? window.screen.width;
      deviceInfo.screenHeight = deviceInfo.screenHeight ?? window.screen.height;

      if (!deviceInfo.hdr) {
        try {
          const rawHDR = (window as any).Hisense_GetSupportForHDR?.();
          if (rawHDR !== undefined) {
            const hdrCaps = parseVidaaHDRSupport(rawHDR);
            if (hdrCaps.hdr) {
              deviceInfo.hdr = true;
              if (!deviceInfo.hdr10) deviceInfo.hdr10 = hdrCaps.hdr10 || undefined;
              if (!deviceInfo.hlg) deviceInfo.hlg = hdrCaps.hlg || undefined;
              if (!deviceInfo.hdr10Plus) deviceInfo.hdr10Plus = hdrCaps.hdr10Plus || undefined;
            }
          }
        } catch {
          // Direct Hisense_GetSupportForHDR not available or threw
        }
      }
    }
    // UHD heuristic: if platform APIs didn't report HDR but panel is 4K, assume HDR (same as Tizen)
    if (deviceInfo.hdr === undefined && deviceInfo.uhd === true) {
      deviceInfo.hdr = true;
      if (!deviceInfo.hdr10 && !deviceInfo.dolbyVision) deviceInfo.hdr10 = true;
    }

    // Policy: VIDAA must report no HDR (HDR not allowed for VIDAA).
    deviceInfo.hdr = false;
    deviceInfo.hdr10 = undefined;
    deviceInfo.hlg = undefined;
    deviceInfo.hdr10Plus = undefined;
    deviceInfo.dolbyVision = undefined;
    // Policy: VIDAA must report no HDR (HDR not allowed for VIDAA).

    return deviceInfo;
  } catch (error) {
    console.error('[VIDAA] Failed to get device info:', error);
    return {
      platform: 'vidaa',
      screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
      screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
    };
  }
};

/**
 * Query the webOS Luna service for HDR capabilities.
 * Used when webOS.deviceInfo() returns without hdr10/dolbyVision (older webOS).
 * Luna key hdrType is supported by the TV system property service on webOS 3+.
 */
const queryLunaHDR = (base: DeviceInfo, resolve: (value: DeviceInfo) => void): void => {
  const webOS = typeof window !== 'undefined' && (window as any).webOS ? (window as any).webOS : null;

  if (!webOS?.service?.request) {
    applyWebOSUhdHeuristic(base);
    resolve(base);
    return;
  }

  const lunaTimeout = setTimeout(() => {
    applyWebOSUhdHeuristic(base);
    resolve(base);
  }, 3000);

  webOS.service.request('luna://com.webos.service.tv.systemproperty', {
    method: 'getSystemInfo',
    parameters: { keys: ['modelName', 'sdkVersion', 'UHD', 'hdrType'] },
    onComplete: (inResponse: {
      returnValue?: boolean;
      UHD?: boolean;
      modelName?: string;
      sdkVersion?: string;
      hdrType?: string;
    }) => {
      clearTimeout(lunaTimeout);
      if (inResponse.returnValue) {
        if (inResponse.hdrType) {
          const hdrType = String(inResponse.hdrType).toLowerCase();
          base.hdr = true;
          base.dolbyVision = hdrType.includes('dolby');
          base.hdr10 = hdrType.includes('hdr10');
          base.hlg = hdrType.includes('hlg');
        }
        if (inResponse.UHD !== undefined) base.uhd = !!inResponse.UHD;
        base.modelName = base.modelName ?? inResponse.modelName;
        base.sdkVersion = base.sdkVersion ?? inResponse.sdkVersion;
      }
      base.hdr = base.hdr ?? !!(base.hdr10 || base.hlg || base.dolbyVision);
      applyWebOSUhdHeuristic(base);
      resolve(base);
    },
  });
};

/** When Luna/deviceInfo didn't set HDR but panel is UHD, assume HDR (same heuristic as Tizen/VIDAA). */
function applyWebOSUhdHeuristic(base: DeviceInfo): void {
  if (base.hdr === undefined && base.uhd === true) {
    base.hdr = true;
    if (!base.hdr10 && !base.dolbyVision) base.hdr10 = true;
  }
}

/**
 * Get WebOS device information.
 * 1. webOS.deviceInfo() is primary; returns hdr10 and dolbyVision per docs.
 * 2. If those fields are undefined (older webOS), fall back to Luna queryLunaHDR.
 * 3. If deviceInfo() never fires (>3s), go to Luna.
 */
const getWebOSDeviceInfo = (): Promise<DeviceInfo> => {
  return new Promise(resolve => {
    const deviceInfo: DeviceInfo = {
      platform: 'webos',
      manufacturer: 'LG',
    };

    let resolved = false;
    const safeResolve = (info: DeviceInfo) => {
      if (!resolved) {
        resolved = true;
        resolve(info);
      }
    };

    if (typeof window === 'undefined' || !(window as any).webOS) {
      safeResolve(deviceInfo);
      return;
    }

    const webOS = (window as any).webOS;

    if (webOS.deviceInfo) {
      const callbackTimeout = setTimeout(() => {
        if (typeof window !== 'undefined') {
          deviceInfo.screenWidth = window.screen.width;
          deviceInfo.screenHeight = window.screen.height;
        }
        queryLunaHDR(deviceInfo, safeResolve);
      }, 3000);

      try {
        webOS.deviceInfo((info: WebOSDeviceInfo) => {
          clearTimeout(callbackTimeout);
          const base: DeviceInfo = {
            ...deviceInfo,
            modelName: info.modelName,
            version: info.version,
            sdkVersion: info.sdkVersion,
            screenWidth: info.screenWidth,
            screenHeight: info.screenHeight,
            uhd: info.uhd,
            uhd8K: info.uhd8K,
            dolbyAtmos: info.dolbyAtmos,
          };

          const hdr10 = info.hdr10;
          const dolbyVision = info.dolbyVision;

          base.hdr10 = hdr10;
          base.dolbyVision = dolbyVision;
          base.hdr = !!(hdr10 === true || dolbyVision === true);

          if (base.hdr) {
            safeResolve(base);
          } else {
            queryLunaHDR(base, safeResolve);
          }
        });
      } catch (error) {
        clearTimeout(callbackTimeout);
        queryLunaHDR(deviceInfo, safeResolve);
      }
    } else if ((window as any).PalmSystem) {
      const palmSystem = (window as any).PalmSystem;
      try {
        const info: PalmSystemDeviceInfo = JSON.parse(palmSystem.deviceInfo || '{}');
        deviceInfo.modelName = info.modelName;
        deviceInfo.version = info.platformVersion;
        deviceInfo.screenWidth = info.screenWidth ?? window.screen.width;
        deviceInfo.screenHeight = info.screenHeight ?? window.screen.height;
      } catch (e) {
        console.warn('[WebOS] Failed to parse PalmSystem deviceInfo:', e);
      }
      queryLunaHDR(deviceInfo, safeResolve);
    } else {
      safeResolve(deviceInfo);
    }
  });
};

/**
 * Get Tizen system capabilities
 * @param capability System capability type
 * @returns Promise<System capability value>
 */
const showCapability = (capability: 'BUILD' | 'DISPLAY' | 'ETHERNET_NETWORK'): Promise<Record<string, any>> => {
  return new Promise(resolve => {
    const info: Record<string, any> = {};

    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      console.warn(`[Tizen] ${capability} capability timeout after 2 seconds, using fallback`);
      resolve(info);
    }, 2000);

    try {
      window.tizen?.systeminfo?.getPropertyValue(
        capability,
        function (data) {
          clearTimeout(timeout);
          Object.getOwnPropertyNames(data).forEach(function (propName) {
            info[propName] = data[propName];
          });
          resolve(info);
        },
        (error: Error) => {
          clearTimeout(timeout);
          console.warn(`Failed to get Tizen ${capability} info:`, error);
          // Resolve with empty info instead of rejecting to prevent blocking
          resolve(info);
        },
      );
    } catch (error) {
      clearTimeout(timeout);
      console.warn(`Exception getting Tizen ${capability}:`, error);
      resolve(info);
    }
  });
};

/**
 * Get Tizen device information.
 * HDR: Samsung ProductInfo API does not expose isHdrTvSupport; use webapis.avinfo.isHdrTvSupport() per Samsung docs.
 * Dolby Vision cannot be detected via Tizen web APIs; deviceInfo.dolbyVision may remain undefined on DV-capable Samsungs.
 */
const getTizenDeviceInfo = async (): Promise<DeviceInfo> => {
  const deviceInfo: DeviceInfo = {
    platform: 'tizen',
    manufacturer: 'Samsung',
  };

  if (typeof window !== 'undefined' && window.tizen) {
    const tizen = window.tizen;

    try {
      // 1. HDR: Use AVInfo API (ProductInfo does not have isHdrTvSupport per Samsung docs)
      const avinfo = (window as any).webapis?.avinfo ?? (tizen as any).avinfo;
      if (avinfo && typeof avinfo.isHdrTvSupport === 'function') {
        try {
          const avHdr = avinfo.isHdrTvSupport();
          if (typeof avHdr === 'boolean') {
            deviceInfo.hdr = avHdr;
            if (avHdr) deviceInfo.hdr10 = true;
          }
        } catch {
          // AVInfo not available or threw on this model
        }
      }

      // 2. ProductInfo: model, version, deviceId, UHD only (no HDR/DV methods in ProductInfo API)
      if (window.webapis?.productinfo) {
        const productInfo = window.webapis.productinfo;
        deviceInfo.modelName = productInfo.getRealModel();
        deviceInfo.version = productInfo.getVersion();
        deviceInfo.deviceId = productInfo.getDuid();
        deviceInfo.uhd = productInfo.isUdPanelSupported();
      }

      // 3. SystemInfo: resolution, optional capability checks, UHD heuristic
      if (tizen.systeminfo) {
        const display = await showCapability('DISPLAY');
        deviceInfo.screenWidth = display.resolutionWidth;
        deviceInfo.screenHeight = display.resolutionHeight;
        const capabilities: TizenCapabilities = tizen?.systeminfo?.getCapabilities?.();
        if (deviceInfo.hdr === undefined && typeof capabilities?.hdr === 'boolean') {
          deviceInfo.hdr = capabilities.hdr;
          if (deviceInfo.hdr) deviceInfo.hdr10 = true;
        }
        if (deviceInfo.hdr === undefined && typeof (tizen.systeminfo as any).getCapability === 'function') {
          try {
            const capDisplay = (tizen.systeminfo as any).getCapability('DISPLAY');
            const capHdr = capDisplay && typeof capDisplay === 'object' && (capDisplay as any).hdr;
            if (typeof capHdr === 'boolean') {
              deviceInfo.hdr = capHdr;
              if (capHdr) deviceInfo.hdr10 = true;
            }
          } catch {
            // getCapability('DISPLAY') throws WebAPIException on some Samsung models
          }
        }
        if (deviceInfo.hdr === undefined && deviceInfo.uhd === true) {
          deviceInfo.hdr = true;
          deviceInfo.hdr10 = true;
        }
        const build = await showCapability('BUILD');
        deviceInfo.modelName = deviceInfo.modelName || build.model;
        deviceInfo.version = deviceInfo.version || build.buildVersion;
        const network = await showCapability('ETHERNET_NETWORK');
        deviceInfo.mac = network?.macAddress;
      }
    } catch (error) {
      console.warn('Failed to get Tizen device info:', error);
    }
  }

  if (typeof window !== 'undefined') {
    deviceInfo.screenWidth = deviceInfo.screenWidth || window.screen.width;
    deviceInfo.screenHeight = deviceInfo.screenHeight || window.screen.height;
  }

  return deviceInfo;
};

/**
 * Detect current platform.
 *
 * Detection order (most-reliable → least-reliable):
 *  1. Hisense native globals  — conclusive VIDAA indicator, injected by the OS itself.
 *     UA strings vary by model and firmware; globals are always present when running on VIDAA.
 *  2. webOS / PalmSystem window objects — injected by webOSTV.js (WebOS builds only).
 *  3. Tizen / webapis window objects   — injected by the Tizen SDK.
 *  4. User-Agent string checks         — last resort; VIDAA checked before SmartTV.
 *
 * @returns {'webos' | 'tizen' | 'vidaa' | 'unknown'} Platform type
 */
export const detectPlatform = (): 'webos' | 'tizen' | 'vidaa' | 'playstation' | 'unknown' => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  // 1. Hisense globals — most reliable VIDAA signal, independent of UA string format.
  //    Injected by the VIDAA OS on every model regardless of firmware version.
  if (
    typeof (window as any).Hisense_GetModelName === 'function' ||
    typeof (window as any).Hisense_GetBrand === 'function' ||
    typeof (window as any).Hisense_GetDeviceID === 'function'
  ) {
    return 'vidaa';
  }

  // 2. WebOS SDK global — only present when webOSTV.js is loaded (WebOS builds only).
  if ((window as any).webOS || (window as any).PalmSystem) {
    return 'webos';
  }

  // 3. Tizen SDK globals — injected by the Tizen WebAPI loader.
  if ((window as any).tizen || (window as any).webapis) {
    return 'tizen';
  }

  // 4. User-Agent fallback — VIDAA before SmartTV to avoid false webOS match.
  const ua = navigator.userAgent;
  const uaLower = ua.toLowerCase();

  // 'SmartTV' (no hyphen) is intentionally absent from the webOS branch:
  // Hisense VIDAA UAs contain 'SmartTV' in their payload, so using it as a webOS
  // signal would cause false matches. LG WebOS UAs always include 'webOS'/'webos'.
  // 'SMART-TV' (hyphenated) is Samsung-specific and safe to keep in the Tizen branch.
  if (uaLower.includes('vidaa') || uaLower.includes('hisense')) {
    return 'vidaa';
  }
  if (ua.includes('webOS') || uaLower.includes('webos')) {
    return 'webos';
  }
  if (ua.includes('Tizen') || ua.includes('SMART-TV') || uaLower.includes('tizen')) {
    return 'tizen';
  }

  if (uaLower.includes('playstation') || uaLower.includes('ps4') || uaLower.includes('ps5')) {
    return 'playstation';
  }

  return 'unknown';
};

/**
 * Derives a human-readable model name from the User-Agent when platform APIs
 * didn't provide one. Returns a platform-appropriate generic label as a last resort.
 */
function deriveModelNameFromUA(platform: DeviceInfo['platform']): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  switch (platform) {
    case 'webos': {
      const m = ua.match(/webOS\.TV-([\d.]+)/i) || ua.match(/webOSTV\/([\d.]+)/i);
      return m ? `LG TV (webOS ${m[1]})` : 'LG TV';
    }
    case 'tizen': {
      const m = ua.match(/Tizen\s*([\d.]+)/i);
      return m ? `Samsung TV (Tizen ${m[1]})` : 'Samsung TV';
    }
    case 'vidaa': {
      const m = ua.match(/Hisense[_/\s]+([\S]+)/i);
      return m ? `Hisense ${m[1]}` : 'Hisense TV';
    }
    case 'playstation': {
      if (ua.includes('PS5') || ua.includes('PlayStation 5')) return 'PlayStation 5';
      if (ua.includes('PS4') || ua.includes('PlayStation 4')) return 'PlayStation 4';
      return 'PlayStation';
    }
    default: {
      const chrome = ua.match(/Chrome\/([\d]+)/);
      if (chrome) return `Browser (Chrome ${chrome[1]})`;
      const firefox = ua.match(/Firefox\/([\d]+)/);
      if (firefox) return `Browser (Firefox ${firefox[1]})`;
      if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari Browser';
      return 'Smart TV';
    }
  }
}

/**
 * Ensures manufacturer and modelName always have meaningful values before the
 * result is cached or returned. Prevents "undefined undefined" strings when
 * platform APIs are unavailable or fail.
 */
function resolveDeviceIdentity(info: DeviceInfo): void {
  if (!(info as any).manufacturer) {
    if (info.platform === 'webos') (info as any).manufacturer = 'LG';
    else if (info.platform === 'tizen') (info as any).manufacturer = 'Samsung';
    else if (info.platform === 'vidaa') (info as any).manufacturer = 'Hisense';
  }

  if (!info.modelName) {
    info.modelName = deriveModelNameFromUA(info.platform);
  }
}

/**
 * Get device information
 * Priority reading from localStorage, if not exists, call the corresponding platform system method to get
 * @returns {Promise<DeviceInfo>} Device information
 */
export const getDeviceInfo = async (cached = false): Promise<DeviceInfo> => {
  if (cached) {
    // Wait for any in-flight uncached fetch so we never return stale storage
    if (deviceInfoFetchPromise) {
      await deviceInfoFetchPromise;
    }
    const cachedInfo = getStorage<DeviceInfo>(STORAGE_KEYS.DEVICE_INFO);
    if (cachedInfo) {
      return cachedInfo;
    }
  }

  // Detect platform
  const platform = detectPlatform();
  let deviceInfo: DeviceInfo;

  let resolveFetch: (info: DeviceInfo) => void;
  deviceInfoFetchPromise = new Promise<DeviceInfo>(resolve => {
    resolveFetch = resolve;
  });
  try {
    switch (platform) {
      case 'webos':
        deviceInfo = await getWebOSDeviceInfo();
        break;
      case 'tizen':
        deviceInfo = await getTizenDeviceInfo();
        break;
      case 'vidaa':
        deviceInfo = await getVidaaDeviceInfo();
        break;
      default:
        deviceInfo = {
          platform,
          screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
          screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
        };
    }

    applyHdrWebFallback(deviceInfo);
    deviceInfo.hdr =
      deviceInfo.hdr ?? !!(deviceInfo.hdr10 || deviceInfo.hlg || deviceInfo.hdr10Plus || deviceInfo.dolbyVision);
    // Policy: VIDAA must report no HDR (HDR not allowed for VIDAA).
    if (platform === 'vidaa') {
      deviceInfo.hdr = false;
      deviceInfo.hdr10 = undefined;
      deviceInfo.hlg = undefined;
      deviceInfo.hdr10Plus = undefined;
      deviceInfo.dolbyVision = undefined;
    }
    // Policy: VIDAA must report no HDR (HDR not allowed for VIDAA).
    resolveDeviceIdentity(deviceInfo);
    setStorage(STORAGE_KEYS.DEVICE_INFO, deviceInfo);
    forceFlushStorage();
    resolveFetch!(deviceInfo);
    return deviceInfo;
  } catch (error) {
    console.error('Failed to get device info:', error);
    const fallbackInfo: DeviceInfo = {
      platform,
      screenWidth: typeof window !== 'undefined' ? window.screen.width : undefined,
      screenHeight: typeof window !== 'undefined' ? window.screen.height : undefined,
    };
    applyHdrWebFallback(fallbackInfo);
    fallbackInfo.hdr =
      fallbackInfo.hdr ??
      !!(fallbackInfo.hdr10 || fallbackInfo.hlg || fallbackInfo.hdr10Plus || fallbackInfo.dolbyVision);
    resolveDeviceIdentity(fallbackInfo);
    setStorage(STORAGE_KEYS.DEVICE_INFO, fallbackInfo);
    forceFlushStorage();
    resolveFetch!(fallbackInfo);
    return fallbackInfo;
  } finally {
    deviceInfoFetchPromise = null;
  }
};

/**
 * Clear device information cache
 * Used to force re-acquiring device information
 */
export const clearDeviceInfoCache = (): void => {
  removeStorage(STORAGE_KEYS.DEVICE_INFO);
};

/**
 * Determine if it is a Smart TV
 * @returns {boolean} Whether it is a Smart TV
 */
export const isSmartTV = (): boolean => {
  const platform = detectPlatform();
  return platform === 'webos' || platform === 'tizen';
};

/** Query param value to limit manifest to non-4K ladder (1080p max). Use with aws.manifestfilter. */
export const MANIFEST_FILTER_NON_4K = 'video_height:1-1080';

/**
 * Estimate the physical screen width, accounting for devicePixelRatio on
 * Smart TVs that report CSS pixels (e.g. 1920) for a 4K panel.
 */
const estimatePhysicalScreenWidth = (): number | undefined => {
  if (typeof window === 'undefined') return undefined;
  const cssWidth = window.screen.width;
  if (!cssWidth) return undefined;
  const dpr = window.devicePixelRatio || 1;
  return Math.round(cssWidth * dpr);
};

/**
 * Determine if 4K is supported.
 *
 * Uses cached device info (populated during app bootstrap in main.ts) to
 * avoid repeated async platform API calls and their associated timeout risks.
 * Falls back to a fresh lookup if no cache is available yet.
 *
 * Safe default: returns **false** (assume non-4K) when detection fails,
 * so the manifest filter is applied and higher-than-1080p renditions are
 * excluded rather than streamed to a device that cannot display them.
 *
 * @returns {Promise<boolean>} Whether 4K is supported
 */
export const isUHDSupported = async (): Promise<boolean> => {
  try {
    // Prefer cached device info to avoid redundant platform API calls / timeouts
    const deviceInfo = await getDeviceInfo(true);

    // Explicit platform UHD flag (most reliable when available)
    if (deviceInfo.uhd === true) return true;

    // Fallback: check physical screen width (accounts for devicePixelRatio)
    const physicalWidth = estimatePhysicalScreenWidth();
    if (physicalWidth !== undefined && physicalWidth >= 3840) return true;

    // Also check the stored screenWidth (may already be physical on Tizen)
    if (deviceInfo.screenWidth !== undefined && deviceInfo.screenWidth >= 3840) return true;

    return false;
  } catch {
    // Safe default: assume non-4K when detection fails
    console.warn('[Device] isUHDSupported detection failed, defaulting to non-4K');
    return false;
  }
};

/**
 * Determine if HDR is supported
 * @returns {Promise<boolean>} Whether HDR is supported
 */
export const isHDRSupported = async (): Promise<boolean> => {
  const deviceInfo = await getDeviceInfo(true);
  return deviceInfo?.hdr === true;
};

/**
 * Comma-separated video capabilities for stream API (e.g. videoCapabilities=hdr10,hlg,dolbyVision).
 * Uses the same effective device info as the on-screen debugger (including web fallback for platform unknown).
 */
export const getVideoCapabilitiesForStreamApi = async (): Promise<string> => {
  const { videoCapabilities } = await getStreamRequestDeviceInfo();
  return videoCapabilities;
};

/**
 * Device info for the stream API request: videoCapabilities query param and model/brand headers.
 * Reads from localStorage (populated at startup in main.ts). No extra wait or platform calls.
 */
export const getStreamRequestDeviceInfo = async (): Promise<{
  videoCapabilities: string;
  model: string;
  brand: string;
}> => {
  const platform = detectPlatform();
  const raw = await getDeviceInfo(true);
  const info = raw ?? ({} as DeviceInfo);

  // Policy: VIDAA must never send HDR capabilities to the API (enforced here in case cache is stale).
  let videoCapabilities: string;
  if (platform === 'vidaa') {
    videoCapabilities = '';
  } else {
    let effectiveHdr10 = info.hdr10;
    let effectiveHlg = info.hlg;
    const effectiveDolbyVision = info.dolbyVision;

    if (platform === 'unknown' && typeof window !== 'undefined') {
      const webFallback = getWebStandardHDRFallback();
      effectiveHdr10 = effectiveHdr10 ?? webFallback.p3;
      if (webFallback.hdr && effectiveHdr10 !== true) effectiveHdr10 = true;
      if (webFallback.hdr && effectiveHlg !== true) effectiveHlg = true;
    }

    const parts: string[] = [];
    if (effectiveHdr10 === true) parts.push('hdr10');
    if (effectiveHlg === true) parts.push('hlg');
    if (effectiveDolbyVision === true) parts.push('dolbyVision');
    // Align with debugger: if cache has generic hdr: true but no specific type, send hdr10 so API returns HDR
    if (parts.length === 0 && info.hdr === true) parts.push('hdr10');
    videoCapabilities = parts.join(',');
  }

  const modelName = (info.modelName && info.modelName.trim()) || '';
  const brandFromPlatform =
    platform === 'tizen' ? 'Samsung' : platform === 'webos' ? 'LG' : platform === 'vidaa' ? 'Hisense' : 'Unknown';
  const model = modelName || brandFromPlatform;
  const brand = (info.manufacturer as string) || brandFromPlatform;
  return { videoCapabilities, model, brand };
};

/**
 * Device capabilities for debugger (UHD, HDR type, Dolby Vision).
 * Reads from localStorage (populated at startup). Applies web fallback when cached info has no HDR (e.g. WebOS build in browser).
 */
export const getDeviceCapabilitiesForDebug = async (): Promise<{
  uhd: boolean;
  hdr: boolean;
  hdrTypes: string[];
  dolbyVision: boolean;
  dolbyAtmos: boolean;
  summary: string;
}> => {
  const platform = detectPlatform();
  let info: DeviceInfo = (await getDeviceInfo(true)) ?? ({} as DeviceInfo);

  if (platform === 'unknown' && typeof window !== 'undefined') {
    const webFallback = getWebStandardHDRFallback();
    const physicalWidth = estimatePhysicalScreenWidth();
    info = {
      ...info,
      uhd: info.uhd ?? (physicalWidth !== undefined && physicalWidth >= 3840),
      hdr: info.hdr ?? webFallback.hdr,
      hdr10: info.hdr10 ?? webFallback.p3,
    };
  }

  // Policy: VIDAA must always show HDR: No in debugger (enforced here in case cache is stale).
  if (platform === 'vidaa') {
    info = { ...info, hdr: false, hdr10: undefined, hlg: undefined, hdr10Plus: undefined, dolbyVision: undefined };
  }

  const dolbyVision = info.dolbyVision === true;
  const hdr10 = info.hdr10 === true;
  const hdr10Plus = info.hdr10Plus === true;
  const uhd = info.uhd === true;
  const dolbyAtmos = info.dolbyAtmos === true;
  const hdrTypes: string[] = [];
  if (dolbyVision) hdrTypes.push('Dolby Vision');
  if (hdr10Plus) hdrTypes.push('HDR10+');
  if (hdr10) hdrTypes.push('HDR10');
  if (info.hdr && hdrTypes.length === 0) hdrTypes.push('HDR');
  const hdr = info.hdr === true || dolbyVision || hdr10 || hdr10Plus;
  const parts = [
    `4K/UHD: ${uhd ? 'Yes' : 'No'}`,
    `HDR: ${hdr ? 'Yes' : 'No'}`,
    hdrTypes.length ? `(${hdrTypes.join(', ')})` : '',
    `Dolby Vision: ${dolbyVision ? 'Yes' : 'No'}`,
    `Dolby Atmos: ${dolbyAtmos ? 'Yes' : 'No'}`,
  ].filter(Boolean);
  return {
    uhd,
    hdr,
    hdrTypes,
    dolbyVision,
    dolbyAtmos,
    summary: parts.join(' · '),
  };
};

/**
 * Web standards fallback for HDR/color capability (all platforms).
 * Uses matchMedia('(dynamic-range: high)') for HDR and (color-gamut: p3) for wide gamut.
 * Dolby Vision cannot be detected via web standards; use platform APIs for DV.
 */
function getWebStandardHDRFallback(): { hdr: boolean; p3: boolean; rec2020: boolean } {
  if (typeof window === 'undefined') return { hdr: false, p3: false, rec2020: false };
  return {
    hdr: window.matchMedia('(dynamic-range: high)').matches,
    p3: window.matchMedia('(color-gamut: p3)').matches,
    rec2020: window.matchMedia('(color-gamut: rec2020)').matches,
  };
}

/**
 * Last-resort HDR detection via CSS media query. Only promotes false→true, never demotes.
 * Uses the standard (dynamic-range: high) check; see e.g.
 * https://stackoverflow.com/questions/57400110/check-if-display-supports-hdr-high-dynamic-range-color-in-javascript
 * Call after platform-specific detection when hdr may have been missed.
 */
function applyHdrWebFallback(deviceInfo: DeviceInfo): void {
  if (deviceInfo.hdr) return;
  if (typeof window === 'undefined') return;
  let matchResult = false;
  try {
    matchResult = window.matchMedia('(dynamic-range: high)').matches;
    if (matchResult) {
      deviceInfo.hdr = true;
    }
  } catch {
    // matchMedia not supported or threw
  }
}

/**
 * Register media control keys for Tizen platform
 * Tizen needs to explicitly register MediaFastForward and MediaRewind keys to use them
 * @returns {Promise<boolean>} Whether successfully registered
 */
export const registerTizenMediaKeys = (): Promise<boolean> => {
  return new Promise(resolve => {
    const platform = detectPlatform();

    // Only perform registration on Tizen platform
    if (platform !== 'tizen') {
      console.log('Platform is not Tizen, skipping key registration');
      resolve(true);
      return;
    }

    try {
      // Check if tizen.tvinputdevice is available
      if (typeof window !== 'undefined' && window.tizen?.tvinputdevice) {
        const tvInputDevice = window.tizen.tvinputdevice;

        // Register media control keys
        const mediaKeys = ['MediaFastForward', 'MediaRewind'];

        tvInputDevice.registerKeyBatch(
          mediaKeys,
          (data: any) => {
            console.log('Successfully registered Tizen media keys:', data);
            resolve(true);
          },
          (error: any) => {
            console.error('Failed to register Tizen media keys:', error);
            // Even if registration fails, resolve the promise to let the application continue
            resolve(false);
          },
        );
      } else {
        console.warn('Tizen TV input device API not available');
        resolve(false);
      }
    } catch (error) {
      console.error('Error during Tizen key registration:', error);
      resolve(false);
    }
  });
};

/**
 * Check if Tizen media keys are available
 * @returns {boolean} Whether media keys are available
 */
export const isTizenMediaKeysAvailable = (): boolean => {
  return typeof window !== 'undefined' && window.tizen?.tvinputdevice?.registerKeyBatch !== undefined;
};
